import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {environment} from '../../env';
import {HttpClient, HttpParams} from '@angular/common/http';
import {
  DesiredStockItem,
  Stock,
  StockItem, StockItemWithProduct,
  StockUpdate2,
  StockUpdateByProductId
} from '../../models/stock';
import {DatePipe} from '@angular/common';
import {TemperatureZone} from "../../models/temperature-zone";
import {parseDecimal} from "../../utils/number.util";
import {map} from "rxjs/operators";
import { AppLogger, AppLoggerService } from '../../core/logging';

export type IngredientMatchStatus = 'available' | 'insufficient' | 'uncertain' | 'not_found';

export type AvailabilityStatus = 'out_of_stock' | 'unknown' | 'available' | 'enough' | 'not_enough';

export type UnitType = 'weight' | 'volume' | 'piece' | string;

export interface StockAvailableTotal {
  value: number;
  unit: string;
  unit_type: UnitType;
}

export interface StockAvailableInfo {
  status: AvailabilityStatus;
  is_available: boolean;
  instances_total: number;
  instances_open?: number;
  instances_sealed?: number;
  total_available?: StockAvailableTotal;
}

export interface IngredientMatchProduct {
  product_id: number;
  name: string;
  brands?: string;
  thumb_img_url?: string;
  match_type: string;
  reason?: string;
  stock_available?: StockAvailableInfo;
}

export interface IngredientMatchResponse {
  ingredient: {
    ingredient_name: string;
    quantity_raw?: string;
  };
  status: IngredientMatchStatus;
  strategy: string;
  message?: string;
  matches: IngredientMatchProduct[];
}

@Injectable({
  providedIn: 'root'
})
export class StockService {

  private readonly log: AppLogger;

  constructor(private http: HttpClient, private datePipe: DatePipe, appLogger: AppLoggerService) {
    this.log = appLogger.createForClass(StockService);
  }

  stockByStore(storeId: number): Observable<Stock[]> {
    return this.http.get<Stock[]>(`${environment.apiUrl}/stores/${storeId}/stock`);
  }

  getStockItemsByProductId(productId: number): Observable<StockItem[]> {
    return this.http
      .get<StockItem[]>(`${environment.apiUrl}/products/${productId}/stock`)
      .pipe(
        map(items => items.map(item => this.transform(item)))
      );
  }

  getStockItemsByProductIdFilteredByStoreId(productId: number, storeId: number): Observable<StockItem[]> {
    return this.http
      .get<StockItem[]>(`${environment.apiUrl}/products/${productId}/stock?storeId=${storeId}`)
      .pipe(
        map(items => items.map(item => this.transform(item)))
      );
  }

  stockByHousehold(householdId: number): Observable<Stock[]> {
    return this.http.get<Stock[]>(`${environment.apiUrl}/households/${householdId}/stock`);
  }

  stockByHouseholdAndCode(householdId: number, code: string): Observable<Stock> {
    return this.http.get<Stock>(`${environment.apiUrl}/households/${householdId}/stock/${code}`);
  }

  stockByStoreAndCode(storeId: number, code: string): Observable<Stock> {
    return this.http.get<Stock>(`${environment.apiUrl}/stores/${storeId}/stock/${code}`);
  }

  searchStockByName(name: string, householdId: number): Observable<Stock[]> {
    return this.http.get<Stock[]>(`${environment.apiUrl}/households/${householdId}/stock/search?q=${name}`);
  }

  searchStockByStoreAndName(name: string, storeId: number): Observable<Stock[]> {
    return this.http.get<Stock[]>(`${environment.apiUrl}/stores/${storeId}/stock/search?q=${name}`);
  }

  addProductToStock(productId: number, storeId: number, expiryDate?: Date, itemQuantity?: string): Observable<StockItemWithProduct[]> {
    this.log.debug('Add a new product to Store', {storeId, productId, expiryDate, itemQuantity});

    const stockUpdate: StockUpdateByProductId = {
      product_id: productId,
      quantity: 1
    };

    if (expiryDate !== undefined) {
      stockUpdate.expiry_date = this.datePipe.transform(expiryDate, 'yyyy-MM-dd');
    }

    if (itemQuantity) {
      stockUpdate.item_quantity = itemQuantity;
    }

    return this.http.post<StockItemWithProduct[]>(`${environment.apiUrl}/stores/${storeId}/stock/by-product`, stockUpdate);
  }

  addProductToStockByCode(code: string, storeId: number, expiryDate?: Date): Observable<StockItemWithProduct[]> {
    this.log.debug('Add a new product to Stock', {storeId, code, expiryDate});

    const stockUpdate: StockUpdate2 = {
      code: code,
      quantity: 1
    };

    this.log.debug('Service Expiry Date is:', {expiryDate});

    if (expiryDate !== undefined) {
      stockUpdate.expiry_date = this.datePipe.transform(expiryDate, 'yyyy-MM-dd');
    }

    return this.http.post<StockItemWithProduct[]>(`${environment.apiUrl}/stores/${storeId}/stock/by-code`, stockUpdate);
  }

  updateExpiry(stockId: number, expiryDate: Date): Observable<any> {
    const expDate = this.datePipe.transform(expiryDate, 'yyyy-MM-dd');
    const expiryUpdate = {
      expiry_date: expDate
    };

    return this.http.patch<any>(`${environment.apiUrl}/stock-items/${stockId}`, expiryUpdate);
  }

  updateQuantity(stockId: number, quantityLeft: number): Observable<any> {
    return this.http.patch<any>(`${environment.apiUrl}/stock-items/${stockId}`, {
      quantity_left: quantityLeft.toFixed(2)
    });
  }

  updateQuantityFull(stockId: number, quantityFull: number): Observable<any> {
    return this.http.patch<any>(`${environment.apiUrl}/stock-items/${stockId}`, {
      quantity_full: quantityFull.toFixed(2)
    });
  }

  updateTemperatureZone(stockId: number, zone: TemperatureZone): Observable<any> {
    return this.http.patch<any>(`${environment.apiUrl}/stock-items/${stockId}`, {
      temperature_zone: zone
    });
  }

  moveStockItem(stockItemId: number, storeId: number): Observable<any> {
    const body = {
      store_id: storeId
    };

    this.log.debug('Move stock item to store another store', {stockItemId, storeId});
    return this.http.patch<any>(`${environment.apiUrl}/stock-items/${stockItemId}`, body);
  }

  removeProductFromStock(code: string, storeId: number, count?: number): Observable<any> {
    this.log.debug('Remove oldest product from Stock', {storeId, code});
    return this.http.delete<any>(`${environment.apiUrl}/stores/${storeId}/stock/by-code/${code}`);
  }

  removeById(stockId: number): Observable<any> {
    return this.http.delete<any>(`${environment.apiUrl}/stock-items/${stockId}`);
  }

  getDesiredStockByProductId(householdId: number, productId: number): Observable<DesiredStockItem> {
    return this.http.get<DesiredStockItem>(`${environment.apiUrl}/households/${householdId}/desired-stock/by-product/${productId}`);
  }

  updateDesiredStock(householdId: number, productId: number, desiredQuantity: number): Observable<DesiredStockItem> {
    const desiredStockItem: DesiredStockItem = {
      desired: desiredQuantity
    };
    return this.http.put<DesiredStockItem>(`${environment.apiUrl}/households/${householdId}/desired-stock/by-product/${productId}`, desiredStockItem);
  }

  clearDesiredStock(householdId: number, productId: number): Observable<any> {
    this.log.debug('Clear desired stock for product', {productId});
    return this.http.delete<any>(`${environment.apiUrl}/households/${householdId}/desired-stock/by-product/${productId}`);
  }

  /**
   * Check if an ingredient is available in stock
   * GET /households/:id/stock/matches?ingredient=...&quantity=...
   *
   * `quantity` is an arbitrary UI-readable string like "200ml" or "2 tbsp".
   */
  getIngredientStockMatch(
    householdId: number,
    ingredientName: string,
    quantityReadable?: string
  ): Observable<IngredientMatchResponse> {
    let params = new HttpParams().set('ingredient', ingredientName);

    const q = (typeof quantityReadable === 'string') ? quantityReadable.trim() : '';
    if (q.length > 0) {
      params = params.set('quantity', q);
    }

    return this.http.get<IngredientMatchResponse>(
      `${environment.apiUrl}/households/${householdId}/stock/matches`,
      {params}
    );
  }

  private transform(item: StockItem): StockItem {
    return {
      ...item,
      quantity_left: parseDecimal(item.quantity_left),
      quantity_full: parseDecimal(item.quantity_full)
    };
  }

}
